/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';
import {
  getProductUrlForDAM,
  getBrand,
  getLocator,
  routerPush,
  setSessionStorage,
  isMobileWeb,
} from '@tcp/core/src/utils';
import { StyledAddToBagButton } from '@tcp/core/src/components/features/browse/ProductListing/molecules/ProductList/styles/ProductsGridItem.style.js';
import pushEventToAdobeAnalytics from '@tcp/core/src/adobeAnalytics/adobe.utils';
import Anchor from '../../../../common/atoms/Anchor';
import BodyCopy from '../../../../common/atoms/BodyCopy';
import Col from '../../../../common/atoms/Col';
import DamImage from '../../../../common/atoms/DamImage';
import withStyles from '../../../../common/hoc/withStyles';
import { PRODUCT_PRICE_BODY_COPY_COLOR } from '../../../../../constants/brandSpecific.constants';
import PriceCurrency from '../../../../common/molecules/PriceCurrency';
import { ProductMerchantPrice } from '../../../browse/ProductListing/molecules/ProductList/styles/CommonStyle';
import ProductRating from '../../../browse/ProductDetail/molecules/ProductRating/ProductRating';
import styles from '../styles/BIAProductTile.style';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '../../../../../utils/labels/labelUtilty';
import BuyItAgainBagPageLabels, { PLP_LABELS } from '../views/BuyItAgainBagPage.labels';

const renderBrandTitle = (
  isShowBrandNameEnabled,
  tcpBrand,
  gymBrand,
  snjBrand,
  pjpBrand,
  primaryBrand
) => {
  const prodBrand = getBrand() && getBrand().toUpperCase();
  let brandTitle;
  if ((primaryBrand || prodBrand) === 'GYM') {
    brandTitle = gymBrand;
  } else if ((primaryBrand || prodBrand) === 'TCP') {
    brandTitle = tcpBrand;
  } else if ((primaryBrand || prodBrand) === 'SNJ') {
    brandTitle = snjBrand;
  } else if ((primaryBrand || prodBrand) === 'PJP') {
    brandTitle = pjpBrand;
  }
  return isShowBrandNameEnabled ? (
    <BodyCopy
      fontSize="fs12"
      fontWeight="bold"
      fontFamily="secondary"
      className="product-brand-plp"
    >
      {brandTitle}
    </BodyCopy>
  ) : null;
};

const handleAddedToBagClick = ({
  uniqueId,
  onQuickViewOpenClick,
  trackAnalyticsClick,
  amountOfBIAProducts,
  index,
  page,
}) => {
  trackAnalyticsClick({ metric63: '1', customEvents: ['event63'] });
  onQuickViewOpenClick({
    colorProductId: uniqueId,
    fromPage: page,
    dimension66: `bia-bag|items-${amountOfBIAProducts}|position-${index + 1}`,
  });
};

const renderAddToBagButton = (
  uniqueId,
  onQuickViewOpenClick,
  label,
  trackAnalyticsClick,
  amountOfBIAProducts,
  index,
  product,
  page
) => {
  return (
    <div className="btn-container">
      <StyledAddToBagButton
        item={product}
        className="added-to-bag bia-atb"
        id={`brand-${getBrand()}`}
        fullWidth
        buttonVariation="fixed-width"
        dataLocator={getLocator('global_addtocart_Button')}
        onClick={() =>
          handleAddedToBagClick({
            uniqueId,
            onQuickViewOpenClick,
            trackAnalyticsClick,
            amountOfBIAProducts,
            index,
            page,
          })
        }
        ariaLabel={label}
      >
        {label}
      </StyledAddToBagButton>
    </div>
  );
};

const renderLastPurchasedSize = ({ lastPurchasedSize }) => {
  return (
    <BodyCopy
      component="div"
      fontSize={isMobileWeb() ? 'fs10' : 'fs11'}
      fontWeight="normal"
      fontFamily="secondary"
      color="gray.800"
      className={'elem-mb-SM'}
    >{`Last Purchased ${lastPurchasedSize}`}</BodyCopy>
  );
};

const BIAProductTile = (props) => {
  const PlpLabels = getLabelFromCopyOrFromTranslation(PLP_LABELS, BuyItAgainBagPageLabels);
  const {
    product,
    onQuickViewOpenClick,
    className,
    openShopAllDrawer,
    isShopAllDrawerOpen,
    index,
    trackAnalyticsClick,
    amountOfBIAProducts,
    page,
  } = props;
  const IMG_DATA_CLP = {
    imgConfig: ['t_plp_img_m,f_auto', 't_plp_img_t,f_auto', 't_plp_img_d,f_auto'],
  };
  const { productInfo, styleWithData, lastPurchasedSize } = product;
  const {
    name,
    listPrice,
    offerPrice,
    uniqueId = '',
    primaryBrand,
    ratings,
    reviewsCount,
  } = productInfo || {};
  const { seo_token } = styleWithData || {};
  const imageUrl = getProductUrlForDAM(uniqueId);
  const itemBrand = primaryBrand;
  const imageData = {
    alt: name,
    url: getProductUrlForDAM(uniqueId),
  };
  const tcpBrand = "The Children's Place";
  const gymBrand = 'Gymboree';
  const snjBrand = 'Sugar & Jade';
  const pjpBrand = 'PJ Place';
  const brandId = getBrand();
  const navigateToPDP = (e, pdpToPath) => {
    e.preventDefault();
    if (isShopAllDrawerOpen) {
      openShopAllDrawer(false);
    }
    trackAnalyticsClick({
      customEvents: ['event63'],
      metric63: '1',
    });
    const cd66Item = JSON.stringify({
      dimension66: `bia-bag|items-${amountOfBIAProducts}|position-${index + 1}`,
      item: pdpToPath,
    });
    setSessionStorage({ key: 'biaDimension', value: cd66Item });
    const pdpToUrl = `/p?pid=${pdpToPath}`;
    const pdpAsUrl = `/p/${pdpToPath}?fromBIA=1`;
    routerPush(pdpToUrl, pdpAsUrl);
    pushEventToAdobeAnalytics('cross_sell_clicks', 'buy it again');
  };
  return (
    <div className={`${className} parent`}>
      <Col className="product-title-wrapper" colSize={{ small: 3, medium: 4, large: 6 }}>
        <div className="product-tile-cont">
          <div className="bia-crd-img">
            <Anchor handleLinkClick={(e) => navigateToPDP(e, seo_token)} tabIndex="-1" noLink>
              <DamImage
                className="bia-card-image"
                imgData={imageData}
                imgConfigs={IMG_DATA_CLP.imgConfig}
                isProductImage
                imageURL={imageUrl}
                itemBrand={itemBrand}
              />
            </Anchor>
          </div>
          <div className="product-title-container">
            {renderBrandTitle(true, tcpBrand, gymBrand, snjBrand, pjpBrand, itemBrand)}
            <Anchor handleLinkClick={(e) => navigateToPDP(e, seo_token)} tabIndex="-1" noLink>
              <BodyCopy
                fontSize="fs12"
                fontFamily="secondary"
                color={'black'}
                fontWeight={'regular'}
                className="multiline-ellipsis"
              >
                {name}
              </BodyCopy>
            </Anchor>
          </div>
          <div className="price-wrapper">
            <BodyCopy
              color={PRODUCT_PRICE_BODY_COPY_COLOR[brandId.toUpperCase()]}
              fontWeight="extrabold"
              fontFamily="secondary"
              className="container-price"
            >
              <PriceCurrency price={offerPrice} />
            </BodyCopy>
            {offerPrice !== listPrice && (
              <ProductMerchantPrice>
                <BodyCopy
                  component="span"
                  color="gray.700"
                  fontFamily="secondary"
                  fontWeight="semibold"
                  fontSize={'fs9'}
                  className="list-price"
                >
                  <PriceCurrency price={listPrice} />
                </BodyCopy>
              </ProductMerchantPrice>
            )}
          </div>
          <div className="ratings-container">
            <ProductRating className="custom-rating" ratings={ratings} reviews={reviewsCount} />
          </div>
          {lastPurchasedSize && renderLastPurchasedSize({ lastPurchasedSize })}
          {renderAddToBagButton(
            uniqueId,
            onQuickViewOpenClick,
            renderLabel(PlpLabels?.lbl_add_to_bag, 'lbl_add_to_bag'),
            trackAnalyticsClick,
            amountOfBIAProducts,
            index,
            product,
            page
          )}
        </div>
      </Col>
    </div>
  );
};

BIAProductTile.propTypes = {
  product: PropTypes.shape([]),
  onQuickViewOpenClick: PropTypes.func,
  className: PropTypes.string,
  trackAnalyticsClick: PropTypes.func,
  index: PropTypes.number,
  amountOfBIAProducts: PropTypes.number,
};

BIAProductTile.defaultProps = {
  product: [],
  onQuickViewOpenClick: () => {},
  className: 'class',
  trackAnalyticsClick: () => {},
  index: 1,
  amountOfBIAProducts: 1,
};

export default withStyles(BIAProductTile, styles);
export { BIAProductTile as BIAProductTileVanilla };
